import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Building2,
  Truck,
  ShieldCheck,
  Palette,
  Handshake,
  Quote as QuoteIcon,
  Star,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
} from "lucide-react";
import Hero from "../components/Hero";
import { Reveal, MaskedLines, Counter } from "../components/Reveal";
import {
  categories,
  collections,
  projects,
  posts,
  testimonials,
  advantages,
  company,
} from "../data/site";

const partnerLogos = [
  { name: "Anka Seramik", src: "/partners/anka-seramik.png" },
  { name: "Kalekim", src: "/partners/kalekim.png" },
  { name: "Penta", src: "/partners/penta.png" },
  { name: "By Dessen Mobilya", src: "/partners/by-dessen.png" },
  { name: "Essante", src: "/partners/essante.png" },
];

const iconMap = {
  Sparkles,
  Building2,
  Truck,
  ShieldCheck,
  Palette,
  Handshake,
};

export default function Home() {
  const featuredCollections = collections.slice(0, 8);
  const featuredProjects = projects.slice(0, 6);
  const latestPosts = posts.slice(0, 3);

  return (
    <div data-testid="home-page">
      <Hero />

      {/* ————— Manifesto (numbered chapters) ————— */}
      <section className="relative bg-[#FAFAFA] py-16 md:py-40" data-testid="section-manifesto">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-32">
              <Reveal>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-6">
                  Manifesto
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[#1E1E1E] leading-[1.05] tracking-tight">
                  Yüzeyler; <br />
                  <span className="italic">mekânlara verdiğimiz sözdür.</span>
                </h2>
                <p className="mt-5 text-[#666] leading-relaxed text-[14px] max-w-sm">
                  Seçkin seramik ve porselen üreticilerini tek çatı altında buluşturan kürasyon atölyesi.
                </p>
                <Link
                  to="/kurumsal"
                  data-testid="manifesto-cta"
                  className="btn-ghost mt-10"
                >
                  Hikâyemiz <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-8 space-y-24 md:space-y-32">
              {[
                {
                  n: "01",
                  title: "Mükemmellik",
                  text: "Teknoloji, renk ve mimari uyuma göre seçiyoruz.",
                  img: "/catalog/vesna-hero.jpg",
                },
                {
                  n: "02",
                  title: "Zamansızlık",
                  text: "Yıllar sonra bile aynı duyguyu veren yüzeyler.",
                  img: "/catalog/calacatta-hero.jpg",
                },
                {
                  n: "03",
                  title: "Ustalık",
                  text: "Her aşamada teknik detaylara özen gösteriyoruz.",
                  img: "/catalog/dolomite-hero.jpg",
                },
              ].map((c, idx) => (
                <Reveal key={c.n} delay={0.1} y={60}>
                  <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 md:col-span-4 flex md:block items-baseline gap-4">
                      <div className="font-serif-display text-[#B8860B] text-5xl md:text-6xl leading-none">
                        {c.n}
                      </div>
                      <h3 className="font-serif-display text-2xl md:text-3xl text-[#1E1E1E] md:mt-4 leading-tight">
                        {c.title}
                      </h3>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                      <div className="clip-frame aspect-[16/10] mb-6 group">
                        <img
                          src={c.img}
                          alt={c.title}
                          loading="lazy"
                          className="w-full h-full object-cover img-zoom"
                        />
                      </div>
                      <p className="text-[14px] text-[#555] leading-[1.7] max-w-xl">
                        {c.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ————— Marquee ————— */}
      <section
        className="py-10 bg-[#1E1E1E] text-white border-y border-white/5"
        data-testid="section-marquee"
      >
        <Marquee gradient={false} speed={40}>
          {[
            "Premium Yüzeyler",
            "Zamansız Tasarım",
            "Seramik Uzmanlığı",
            "Showroom Kürasyonu",
            "Türkiye Geneli Sevkiyat",
            "20 Yıl Garanti",
            "Bütünsel Yaklaşım",
          ].flatMap((t, i) => [
            <span key={`t-${i}`} className="marquee-item">
              <span className="font-serif-display text-[38px] md:text-[54px] tracking-tight text-white/90 italic">
                {t}
              </span>
              <span className="text-[#B8860B] text-2xl">✦</span>
            </span>,
          ])}
        </Marquee>
      </section>

      {/* ————— Categories (Bento) ————— */}
      <section className="py-16 md:py-32 bg-white" data-testid="section-categories">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-3">
                Kategoriler
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E] leading-tight">
                Doğru yüzey, doğru mekân.
              </h2>
            </Reveal>
          </div>

          <div className="hidden md:grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-6">
            {categories.map((c, idx) => (
              <Link
                key={c.slug}
                to={`/urunler?kategori=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className={`group relative overflow-hidden bg-[#F5F5F5] card-lift aspect-[3/4] md:aspect-auto ${
                  idx === 0
                    ? "md:col-span-3 md:row-span-2 md:aspect-[4/5]"
                    : idx === 1
                    ? "md:col-span-3 md:aspect-[16/9]"
                    : "md:col-span-2 md:aspect-[4/5]"
                }`}
              >
                <img
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                  className="w-full h-full object-cover img-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-white">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#B8860B] mb-2">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif-display text-xl md:text-2xl leading-tight">
                    {c.name}
                  </h3>
                  <div className="mt-2">
                    <ArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Brands ————— */}
      <section className="py-16 md:py-24 bg-white border-y border-black/5" data-testid="section-brands">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10 md:mb-12">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-3">
              Kürasyon Ortaklarımız
            </div>
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#1E1E1E]">
              Sektörün öncü markalarıyla birlikte.
            </h2>
            <div className="mt-5 flex items-center justify-center gap-3">
              <span className="w-10 h-[1px] bg-[#B8860B]" />
              <span className="w-2 h-2 border border-[#B8860B] rotate-45" />
              <span className="w-16 h-[1px] bg-[#B8860B]" />
              <span className="w-2 h-2 bg-[#B8860B] rotate-45" />
              <span className="w-16 h-[1px] bg-[#B8860B]" />
              <span className="w-2 h-2 border border-[#B8860B] rotate-45" />
              <span className="w-10 h-[1px] bg-[#B8860B]" />
            </div>
          </div>
        </div>

        <Marquee gradient={false} speed={65} className="py-4">
          {[...partnerLogos, ...partnerLogos].map((p, i) => (
            <div
              key={`${p.name}-${i}`}
              className="mx-6 md:mx-10 h-16 md:h-20 w-auto flex items-center justify-center"
            >
              <img
                src={p.src}
                alt={p.name}
                loading="lazy"
                className="h-full w-auto max-w-[180px] md:max-w-[220px] object-contain grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
          ))}
        </Marquee>
      </section>

      {/* ————— Latest Collections ————— */}
      <section className="py-16 md:py-32 bg-[#FAFAFA]" data-testid="section-collections">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                Yeni Koleksiyonlar
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E] leading-tight max-w-3xl">
                2026 Kürasyonu
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/urunler"
                className="btn-ghost"
                data-testid="all-collections-cta"
              >
                Tümünü Gör <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          {/* Mobile: horizontal snap carousel, 2 cards visible */}
          <div className="lg:hidden overflow-x-auto snap-x snap-mandatory -mx-6 px-6 pb-4 flex gap-4 scrollbar-hide">
            {featuredCollections.map((c, idx) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="snap-start shrink-0 w-[calc(50%-0.5rem)]"
              >
                <Link
                  to={`/urunler/${c.slug}`}
                  data-testid={`collection-card-mobile-${c.slug}`}
                  className="group block"
                >
                  <div className="clip-frame aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                    <img
                      src={c.hero}
                      alt={c.name}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="pt-3">
                    <div className="text-[9px] tracking-[0.2em] uppercase text-[#888]">
                      {c.finish} · {c.color}
                    </div>
                    <h3 className="mt-1 text-sm font-medium text-[#1E1E1E] group-hover:text-[#B8860B] transition-colors">
                      {c.name}
                    </h3>
                    <div className="mt-0.5 text-[10px] text-[#666]">
                      {c.sizes.slice(0, 2).join(" · ")}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Desktop: grid */}
          <div className="hidden lg:grid grid-cols-4 gap-6">
            {featuredCollections.map((c, idx) => (
              <Reveal key={c.slug} delay={idx * 0.05}>
                <Link
                  to={`/urunler/${c.slug}`}
                  data-testid={`collection-card-${c.slug}`}
                  className="group block"
                >
                  <div className="clip-frame aspect-[4/5] bg-[#F5F5F5] overflow-hidden">
                    <img
                      src={c.hero}
                      alt={c.name}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="pt-5 flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#888]">
                        {c.finish} · {c.color}
                      </div>
                      <h3 className="mt-1 text-lg md:text-xl font-medium text-[#1E1E1E] group-hover:text-[#B8860B] transition-colors">
                        {c.name}
                      </h3>
                      <div className="mt-1 text-[12px] text-[#666]">
                        {c.sizes.join(" · ")}
                      </div>
                    </div>
                    <ArrowRight
                      size={16}
                      className="mt-2 text-[#888] group-hover:text-[#B8860B] transition-all group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Why SeraPark ————— */}
      <section className="py-16 md:py-32 bg-white" data-testid="section-why">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <Reveal>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                  Neden SeraPark
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[#1E1E1E] leading-tight">
                  Deneyim kürasyonu.
                </h2>

                <div className="mt-12 grid grid-cols-2 gap-8">
                  <div>
                    <div className="text-3xl md:text-4xl font-light text-[#1E1E1E]">
                      <Counter to={17} suffix="+" />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      Yıl Deneyim
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-light text-[#1E1E1E]">
                      <Counter to={8000} />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      m² Showroom
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-light text-[#1E1E1E]">
                      <Counter to={520} suffix="+" />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      Referans Proje
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-light text-[#1E1E1E]">
                      <Counter to={40} suffix="+" />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      Marka
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {advantages.map((a, idx) => {
                const Icon = iconMap[a.icon] || Sparkles;
                return (
                  <Reveal key={a.title} delay={idx * 0.05}>
                    <div className="group h-full p-8 bg-[#FAFAFA] border border-black/5 card-lift hover:bg-white transition-colors">
                      <div className="w-12 h-12 border border-[#B8860B]/30 grid place-items-center text-[#B8860B] group-hover:bg-[#B8860B] group-hover:text-white transition-colors">
                        <Icon size={20} strokeWidth={1.4} />
                      </div>
                      <h3 className="mt-6 text-lg font-medium text-[#1E1E1E]">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-[14px] text-[#666] leading-relaxed">
                        {a.text}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ————— Reference Projects (Masonry) ————— */}
      <section
        className="py-16 md:py-32 bg-[#1E1E1E] text-white"
        data-testid="section-projects"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                Referanslar
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-white leading-tight max-w-2xl">
                Referans projeler.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/projeler"
                className="btn-ghost btn-light-ghost"
                data-testid="all-projects-cta"
              >
                Tüm Projeler <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="columns-2 lg:columns-3 gap-4 md:gap-6 space-y-4 md:space-y-6">
            {featuredProjects.map((p, idx) => (
              <Reveal key={p.slug} delay={idx * 0.05}>
                <div className="break-inside-avoid group relative overflow-hidden">
                  <div
                    className={`clip-frame overflow-hidden ${
                      p.height === "tall"
                        ? "aspect-[3/4]"
                        : p.height === "short"
                        ? "aspect-[4/3]"
                        : "aspect-[1/1]"
                    }`}
                  >
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="pt-5 flex items-start justify-between">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#B8860B]">
                        {p.location} · {p.year}
                      </div>
                      <h3 className="mt-1 text-xl font-medium text-white">
                        {p.title}
                      </h3>
                    </div>
                    <div className="flex gap-1.5 flex-wrap justify-end max-w-[45%]">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] tracking-wider uppercase text-white/50 border border-white/15 px-2 py-1"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Instagram grid ————— */}
      <section className="py-16 md:py-32 bg-white" data-testid="section-insta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-3">
                Instagram
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E]">
                @seramikpark.com
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
            {collections.slice(8, 20).map((c, i) => (
              <div key={c.slug} className={`${i >= 6 ? "hidden md:block" : ""}`}>
                <a
                  href="https://www.instagram.com/seramikpark.com"
                  target="_blank"
                  rel="noreferrer"
                  className="group relative aspect-square overflow-hidden bg-[#F5F5F5] block"
                >
                  <img
                    src={c.hero}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover img-zoom"
                  />
                  <div className="absolute inset-0 bg-[#B8860B]/0 group-hover:bg-[#B8860B]/40 transition-colors duration-500" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Testimonials (Glass) ————— */}
      <section
        className="relative py-20 md:py-40 overflow-hidden"
        data-testid="section-testimonials"
      >
        <div className="absolute inset-0">
          <img
            src="/catalog/portoro-hero.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1E1E1E]/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center text-white">
          <Reveal>
            <QuoteIcon size={40} className="mx-auto text-[#B8860B]" strokeWidth={1} />
            <div className="mt-8 grid gap-12">
              {testimonials.map((t, i) => (
                <div key={i} className="glass-dark p-10 md:p-14 border-white/5">
                  <div className="flex justify-center gap-1 mb-6">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} size={14} fill="#B8860B" strokeWidth={0} />
                    ))}
                  </div>
                  <p className="font-serif-display text-xl md:text-2xl leading-relaxed italic text-white/90">
                    "{t.quote}"
                  </p>
                  <div className="mt-6 text-[11px] tracking-[0.3em] uppercase text-[#B8860B]">
                    {t.name} — {t.role}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— Blog preview ————— */}
      <section className="py-16 md:py-32 bg-[#FAFAFA]" data-testid="section-blog">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                Günlük
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E] leading-tight max-w-2xl">
                Yüzeyin sanatı
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/blog" className="btn-ghost" data-testid="all-blog-cta">
                Tüm Yazılar <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {latestPosts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="group block"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <div className="clip-frame aspect-[4/3] bg-[#F5F5F5] overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="mt-6 flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-[#888]">
                    <span className="text-[#B8860B]">{p.category}</span>
                    <span>·</span>
                    <span>{p.readTime}</span>
                  </div>
                  <h3 className="mt-3 font-serif-display text-xl md:text-2xl text-[#1E1E1E] leading-snug group-hover:text-[#B8860B] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[13px] text-[#666] leading-relaxed line-clamp-2">
                    {p.excerpt}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— CTA banner ————— */}
      <section className="relative py-16 md:py-32 bg-[#1E1E1E] text-white overflow-hidden" data-testid="section-cta">
        <div className="absolute inset-0 opacity-30">
          <img
            src="/catalog/calacatta-hero.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-6">
              Projeniz için
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
              Bir mekân,
              <br />
              <span className="italic">bir yüzey uzağınızda.</span>
            </h2>
            <div className="mt-12 flex flex-wrap justify-center gap-3">
              <Link to="/teklif-al" className="btn-gold" data-testid="cta-quote">
                Teklif Al <ArrowRight size={14} />
              </Link>
              <Link to="/iletisim" className="btn-ghost btn-light-ghost" data-testid="cta-contact">
                Showroom Ziyareti
              </Link>
            </div>

            <div className="mt-10">
              <div className="text-[10px] tracking-widest uppercase text-white/50 mb-4">
                Bizi Takip Edin
              </div>
              <div className="flex items-center justify-center gap-3">
                {[
                  { icon: Instagram, href: company.social.instagram, label: "Instagram" },
                  { icon: Facebook, href: company.social.facebook, label: "Facebook" },
                  { icon: Linkedin, href: company.social.linkedin, label: "LinkedIn" },
                  { icon: Youtube, href: company.social.youtube, label: "YouTube" },
                ].map(({ icon: Icon, href, label }) => (
                  <motion.a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    whileHover={{ y: -3 }}
                    className="w-11 h-11 grid place-items-center border border-white/20 text-white hover:border-[#B8860B] hover:text-[#B8860B] transition-colors"
                  >
                    <Icon size={16} />
                  </motion.a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
